// User settings page (v125). Two tabs:
//   SSH keys — for git-over-SSH auth
//   PATs     — personal access tokens (the existing /tokens UI inline)

import { useState } from 'react';
import { Plus, Trash2, KeyRound, Copy, Check, AlertTriangle, Terminal } from 'lucide-react';
import { Card, CardBody, CardFooter, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { cn } from '../lib/cn';
import {
  useSSHKeys,
  addSSHKey,
  deleteSSHKey,
} from '../api/me';
import {
  useTokens,
  createToken,
  revokeToken,
  type CreatedToken,
} from '../api/tokens';

const SCOPES = ['docker:pull', 'docker:push', 'read', 'write', 'admin'];

type Tab = 'ssh' | 'pat';

export function Settings() {
  const [tab, setTab] = useState<Tab>('ssh');

  return (
    <div className="space-y-6">
      <div>
        <p className="orb-eyebrow text-accent/80 mb-2">Account · setup</p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Settings</h1>
        <p className="text-fg-muted text-sm mt-2 max-w-2xl">
          Configure how you authenticate against the API. SSH keys are for
          git-over-SSH; personal access tokens are for HTTPS / Docker /
          npm publishes.
        </p>
      </div>

      <div className="border-b border-border flex">
        {(['ssh', 'pat'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'orb-tab border-b-2',
              tab === t
                ? 'text-accent border-accent'
                : 'text-fg-muted border-transparent hover:text-fg',
            )}
          >
            {t === 'ssh' ? 'SSH keys' : 'Personal access tokens'}
          </button>
        ))}
      </div>

      {tab === 'ssh' ? <SSHKeysTab /> : <PATTab />}
    </div>
  );
}

function SSHKeysTab() {
  const { data: keys, mutate, error } = useSSHKeys();
  const [name, setName] = useState('');
  const [pubKey, setPubKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onAdd = async () => {
    if (!name.trim() || !pubKey.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await addSSHKey({ name: name.trim(), public_key: pubKey.trim() });
      setName('');
      setPubKey('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this SSH key? Any client still using it will start failing immediately.')) return;
    await deleteSSHKey(id);
    await mutate();
  };

  return (
    <>
      <Card>
        <CardHeader title="Add SSH key" description="Paste the contents of your ~/.ssh/id_ed25519.pub (or id_rsa.pub). The key is fingerprinted and stored — the platform never sees your private key." />
        <CardBody className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='name (e.g. "laptop", "work-desktop")' />
          <textarea
            value={pubKey}
            onChange={(e) => setPubKey(e.target.value)}
            placeholder="ssh-ed25519 AAAA…  user@host"
            rows={4}
            className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg placeholder:text-fg-subtle border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-xs font-mono"
          />
          {err && <p className="text-danger font-mono text-xs">{err}</p>}
          {error && (
            <p className="text-fg-muted text-xs flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              {String(error).includes('401') ? 'Sign in to manage SSH keys.' : String(error)}
            </p>
          )}
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button variant="primary" disabled={busy || !name.trim() || !pubKey.trim()} onClick={onAdd}>
            <Plus className="h-4 w-4" />
            Add SSH key
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader title={`Your SSH keys (${keys?.length ?? 0})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Fingerprint</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Added</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Last used</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {keys?.length ? (
                keys.map((k) => (
                  <tr key={k.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                    <td className="px-5 py-3 text-fg flex items-center gap-2">
                      <KeyRound className="h-3.5 w-3.5 text-fg-muted" />
                      {k.name}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-fg-muted">{k.fingerprint}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {new Date(k.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onDelete(k.id)}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No SSH keys registered yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader title="How to use" />
        <CardBody className="space-y-2 text-sm text-fg-muted">
          <p>Generate a key locally:</p>
          <pre className="orb-code text-xs p-3 rounded-md bg-bg-inset block">
{`ssh-keygen -t ed25519 -C "${'<your-email>'}"
cat ~/.ssh/id_ed25519.pub`}
          </pre>
          <p>
            Paste the printed line into the form above. Then your git
            client can authenticate as you against the platform's
            git-over-SSH endpoint.
          </p>
        </CardBody>
      </Card>
    </>
  );
}

function PATTab() {
  const { data: tokens, mutate } = useTokens();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['docker:pull']);
  const [expiresIn, setExpiresIn] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleScope = (s: string) => {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  const onCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const t = await createToken({
        name: name.trim(),
        scopes,
        expiresIn: expiresIn || undefined,
      });
      setCreated(t);
      setName('');
      setExpiresIn('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onRevoke = async (id: string) => {
    if (!confirm('Revoke this token?')) return;
    await revokeToken(id);
    await mutate();
  };

  return (
    <>
      {created && (
        <Card className="border-success/40 bg-success/5">
          <CardHeader title="Token created — copy it now" description="Plaintext is shown ONCE." />
          <CardBody>
            <div className="flex items-center gap-2 p-3 rounded-md bg-bg-inset border border-border font-mono text-sm break-all">
              <Terminal className="h-4 w-4 text-success shrink-0" />
              <span className="flex-1">{created.token}</span>
              <button
                type="button"
                onClick={onCopy}
                className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-border hover:bg-bg-hover text-fg-muted hover:text-fg text-sm"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </CardBody>
          <CardFooter className="flex justify-end">
            <Button variant="secondary" onClick={() => setCreated(null)}>
              Dismiss
            </Button>
          </CardFooter>
        </Card>
      )}

      <Card>
        <CardHeader title="Create personal access token" description="Use as the password in docker login / npm publish / curl -u." />
        <CardBody className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='name (e.g. "laptop", "github-actions")' />
          <div>
            <p className="orb-section-title text-fg-muted mb-2">Scopes</p>
            <div className="flex flex-wrap gap-2">
              {SCOPES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleScope(s)}
                  className={
                    'px-3 h-8 rounded-md text-xs font-mono border ' +
                    (scopes.includes(s)
                      ? 'border-accent text-accent bg-accent/10'
                      : 'border-border text-fg-muted hover:text-fg')
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <Input value={expiresIn} onChange={(e) => setExpiresIn(e.target.value)} placeholder="expires in (Go duration: 24h, 720h, …) — empty = never" />
          {err && <p className="text-danger font-mono text-sm">{err}</p>}
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button variant="primary" disabled={busy || !name.trim() || scopes.length === 0} onClick={onCreate}>
            <Plus className="h-4 w-4" />
            Create token
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader title={`Your tokens (${tokens?.length ?? 0})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Scopes</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Created</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Last used</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {tokens?.length ? (
                tokens.map((t) => (
                  <tr key={t.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                    <td className="px-5 py-3 text-fg">{t.name}</td>
                    <td className="px-5 py-3">
                      {t.scopes.map((s) => (
                        <span key={s} className="orb-code text-[10px] mr-1">{s}</span>
                      ))}
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs">{new Date(t.createdAt).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button type="button" onClick={() => onRevoke(t.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No tokens yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
